import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Text,
  VStack,
  Flex,
  useToast,
  Spinner,
  Box,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { HeaderDataPerson } from "./HeaderDataPerson";
import { MarginEmployerSelect } from "./MarginEmployerSelect";
import { MarginEmployerSelectReportsAnalysis } from "./MarginEmployerSelectReportsAnalysis";
import { BalanceSolicitationAnalysisResponse } from "@/models/consignado/balanceSolicitation.model";
import { getCreditEngineBalanceSolicitationReport } from "@/services/consignado/credit-engine-reports.service";
import { LoadingWebhookEventBmp } from "./LoadingWebhookEventBmp";

interface ConsignadoBalanceSolicitationAnalysisModalProps {
  isOpen: boolean;
  onClose: () => void;
  balanceSolicitationId: string | null;
}

export function ConsignadoBalanceSolicitationAnalysisModal({
  isOpen,
  onClose,
  balanceSolicitationId,
}: ConsignadoBalanceSolicitationAnalysisModalProps) {
  const [analysisData, setAnalysisData] =
    useState<BalanceSolicitationAnalysisResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedVinculoId, setSelectedVinculoId] = useState<string | null>(
    null,
  );

  const toast = useToast();

  useEffect(() => {
    if (isOpen && balanceSolicitationId) {
      fetchAnalysis();
    } else {
      setAnalysisData(null);
      setSelectedVinculoId(null);
    }
  }, [isOpen, balanceSolicitationId]);

  useEffect(() => {
    if (analysisData?.vinculos_empregaticios?.length) {
      setSelectedVinculoId(analysisData.vinculos_empregaticios[0].id);
    }
  }, [analysisData]);

  const fetchAnalysis = async () => {
    if (!balanceSolicitationId) return;

    setIsLoading(true);
    try {
      const data = await getCreditEngineBalanceSolicitationReport(
        balanceSolicitationId,
      );
      setAnalysisData(data);
    } catch (error) {
      console.error("Erro ao obter análise:", error);
      toast({
        title: "Erro ao carregar análise",
        description: "Não foi possível carregar os dados da análise.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="6xl"
      isCentered
      blockScrollOnMount={false}
    >
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(0.625rem)" />
      <ModalContent
        fontFamily="Lexend Deca"
        borderRadius="xl"
        boxShadow="xl"
        maxH="calc(100vh - 2rem)"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <ModalHeader
          borderTopRadius="xl"
          fontSize="xl"
          fontWeight="bold"
          bg="secondary"
          color="white"
        >
          <Flex justify="space-between" align="center">
            <Text>Análise de Crédito</Text>
            <ModalCloseButton
              mt={"0.3rem"}
              color="white"
              position="relative"
              top={0}
              right={0}
            />
          </Flex>
        </ModalHeader>

        <ModalBody
          p={0}
          flex={1}
          display="flex"
          flexDirection="column"
          minH={0}
        >
          <Box
            px={6}
            py={4}
            overflowY="auto"
            flex={1}
            display="flex"
            flexDirection="column"
          >
            {isLoading ? (
              <Flex justify="center" align="center">
                <VStack spacing={4}>
                  <Spinner size="xl" color="secondary" thickness="4px" />
                  <Text fontSize="lg" color="gray.600">
                    Carregando análise...
                  </Text>
                </VStack>
              </Flex>
            ) : !analysisData ? (
              <Text color="gray.500" textAlign="center" py={8}>
                Nenhum dado disponível
              </Text>
            ) : (
              <VStack align="stretch" spacing={6} width="100%" flex={1} minH={0}>
                <HeaderDataPerson dataPerson={analysisData.dados_pessoais} />

                {/* No desktop as colunas ocupam o que sobra da modal (limitada à
                    altura da tela) e rolam por dentro; abaixo de 420px de
                    altura, rola o corpo da modal. */}
                <Flex
                  direction={{ base: "column", lg: "row" }}
                  gap={4}
                  align="stretch"
                  flex={{ base: "none", lg: 1 }}
                  minH={{ base: "auto", lg: "420px" }}
                >
                  <MarginEmployerSelect
                    analysisData={analysisData}
                    setSelectedVinculoId={setSelectedVinculoId}
                    selectedVinculoId={selectedVinculoId}
                  />
                  <MarginEmployerSelectReportsAnalysis
                    analysisData={analysisData}
                    setSelectedVinculoId={setSelectedVinculoId}
                    selectedVinculoId={selectedVinculoId}
                    balanceSolicitationId={balanceSolicitationId || undefined}
                    onStatusUpdate={fetchAnalysis}
                  />
                </Flex>
              </VStack>
            )}
          </Box>

          {balanceSolicitationId && analysisData && (
            <Box
              p={4}
              bg="gray.50"
              borderTop="1px solid"
              borderColor="gray.100"
              flexShrink={0}
            >
              <LoadingWebhookEventBmp
                balanceSolicitationId={balanceSolicitationId}
              />
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
